/**
 * Token_Budgeter — pure history-windowing logic (Requirements 4.4, 4.5).
 *
 * Tokens are estimated, not exact. Gemini's tokenizer is server-side, so we
 * approximate with a `~4 chars / token` heuristic. Because the budget is
 * itself a soft target, this approximation is acceptable per the design.
 *
 * The selection algorithm walks messages newest-to-oldest, accumulating
 * estimated tokens until the next message would push the running total past
 * the remaining budget (after subtracting the system prompt and rolling
 * summary token costs). The accepted slice is returned in chronological
 * order so it can be passed straight to the Gemini `contents` array.
 *
 * This module is pure — no IO, no time, no random — so it is fully amenable
 * to property-based testing (see Property 5 in the design document).
 */

import type { Message } from './types/conversation';

/**
 * Estimate the token count for a piece of text.
 *
 * Uses a `~4 chars / token` heuristic (`Math.ceil(text.length / 4)`).
 * Returns 0 for the empty string.
 */
export function estimateTokens(text: string): number {
  if (text.length === 0) {
    return 0;
  }
  return Math.ceil(text.length / 4);
}

export interface FitWithinBudgetArgs {
  /** Estimated token cost of the rendered System_Prompt. */
  systemPromptTokens: number;
  /** Estimated token cost of the rolling summary, or 0 if none exists. */
  rollingSummaryTokens: number;
  /** Conversation messages in chronological order, oldest first. */
  messages: Message[];
  /** Soft total token budget for the request (system + summary + history). */
  budget: number;
  /**
   * Optional override for the per-message token estimator. Defaults to the
   * built-in {@link estimateTokens}. Injectable to keep this function pure
   * and to let property tests use a deterministic estimator.
   */
  estimateTokens?: (text: string) => number;
}

export interface FitWithinBudgetResult {
  /**
   * The contiguous suffix of `messages` that fits within the budget,
   * returned in chronological order (oldest first).
   */
  included: Message[];
  /** Number of older messages that were dropped from the front. */
  truncatedCount: number;
}

/**
 * Select the most recent messages that fit within the remaining token budget.
 *
 * Property 5 (token budgeter) — the returned slice satisfies:
 *   1. `included` is a contiguous suffix of `messages` (chronological order
 *      is preserved).
 *   2. `systemPromptTokens + rollingSummaryTokens +
 *      sum(estimate(m.content) for m in included) <= budget`.
 *   3. If `truncatedCount > 0`, including the next-older message would have
 *      exceeded `budget`.
 *
 * Validates: Requirements 4.4, 4.5
 */
export function fitWithinBudget(
  args: FitWithinBudgetArgs,
): FitWithinBudgetResult {
  const {
    systemPromptTokens,
    rollingSummaryTokens,
    messages,
    budget,
    estimateTokens: estimate = estimateTokens,
  } = args;

  const remaining = budget - systemPromptTokens - rollingSummaryTokens;

  if (remaining <= 0) {
    return { included: [], truncatedCount: messages.length };
  }

  let running = 0;
  let firstIncludedIndex = messages.length; // exclusive lower bound; nothing included yet

  // Walk newest-to-oldest. Stop as soon as adding the next message would
  // push the running total past `remaining`.
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message === undefined) {
      // Should never happen with a well-formed array; guard for
      // `noUncheckedIndexedAccess`.
      break;
    }
    const cost = estimate(message.content);
    if (running + cost > remaining) {
      break;
    }
    running += cost;
    firstIncludedIndex = i;
  }

  const included = messages.slice(firstIncludedIndex);
  return {
    included,
    truncatedCount: messages.length - included.length,
  };
}
